/* ╔══════════════════════════════════════════════════════════════╗
   ║  Ultra-Modern Shopify Product Gallery — Scripts            ║
   ║  ~4 KB · vanilla JS · IntersectionObserver · touch swipe  ║
   ╚══════════════════════════════════════════════════════════════╝ */

class ProductGallery {
  /**
   * @param {HTMLElement} el — the `.pg-gallery` container
   * @param {Object}      opts — override data-attributes
   */
  constructor(el, opts = {}) {
    this.el = el;
    this.viewer = el.querySelector('.pg-viewer');
    this.track = el.querySelector('.pg-track');
    this.slides = [...el.querySelectorAll('.pg-slide')];
    this.count = this.slides.length;

    // ── Options from data-attrs / overrides ──────────────────
    this.autoplayMs = Number(opts.autoplay ?? el.dataset.autoplay) || 0;
    this.loop = (opts.loop ?? el.dataset.loop) !== 'false';
    this.autoplayVideos = (opts.autoplayVideos ?? el.dataset.autoplayVideos) === 'true';

    // ── State ────────────────────────────────────────────────
    this.index = 0;
    this.autoplayTimer = null;
    this.autoplayPaused = false;
    this.videoPlaying = false;
    this.audioEnabled = false;
    this.progressAnim = null;
    this.touch = { startX: 0, startY: 0, deltaX: 0, time: 0 };
    this.dragging = false;

    // ── Single-slide: only lazy-load, no navigation chrome ────
    if (this.count < 2) {
      this._initSingle();
      return;
    }

    // ── DOM refs (multi-slide only) ──────────────────────────
    this.arrows = {
      prev: el.querySelector('.pg-arrow--prev'),
      next: el.querySelector('.pg-arrow--next'),
    };
    this.dots = [...el.querySelectorAll('.pg-dot')];
    this.thumbs = [...el.querySelectorAll('.pg-thumb')];
    this.progressBar = el.querySelector('.pg-progress');
    this.audioBtn = el.querySelector('.pg-audio-btn');

    // ── Bind & boot ──────────────────────────────────────────
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onProgressEnd = this._onProgressEnd.bind(this);

    this._init();
  }

  // ═══════════════════════════════════════════════════════════════
  //  Public API
  // ═══════════════════════════════════════════════════════════════

  goTo(idx, { animate = true } = {}) {
    if (idx === this.index || idx < 0 || idx >= this.count) {
      if (this.loop && idx < 0) idx = this.count - 1;
      else if (this.loop && idx >= this.count) idx = 0;
      else return;
    }

    const prev = this.index;
    this.index = idx;

    if (!animate) this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${-idx * 100}%)`;
    if (!animate) {
      // Force reflow so the no-transition rule is applied synchronously
      this.track.offsetHeight;
      this.track.style.transition = '';
    }

    this._updateUI(prev);
  }

  next() {
    this.goTo(this.index + 1);
  }

  prev() {
    this.goTo(this.index - 1);
  }

  play() {
    this.autoplayPaused = false;
    this._scheduleAutoplay();
  }

  pause() {
    this.autoplayPaused = true;
    this._clearAutoplay();
  }

  destroy() {
    this._clearAutoplay();
    this._unbindEvents();
    if (this.galleryObs) this.galleryObs.disconnect();
    if (this._extVidTimeout) clearTimeout(this._extVidTimeout);
    this.track.style.transform = '';
    this.track.style.transition = '';
  }

  // ═══════════════════════════════════════════════════════════════
  //  Initialization
  // ═══════════════════════════════════════════════════════════════

  _init() {
    // Load the first slide eagerly (LCP); preload slide 1
    this._loadSlide(0);
    if (this.count > 1) this._loadSlide(1);

    // IntersectionObserver: load slides when gallery nears viewport
    this.galleryObs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this._loadAdjacent();
          if (this.autoplayMs > 0) this.play();
        } else {
          this.pause();
        }
      },
      { rootMargin: '150px 0px' }
    );
    this.galleryObs.observe(this.el);

    // Load thumbnails on hover / near viewport
    this._initThumbLoading();

    // Events
    this._bindEvents();

    // Set initial state
    this._updateUI(-1);

    // Start autoplay if configured
    if (this.autoplayMs > 0 && this._isInViewport()) {
      this.play();
    }
  }

  /**
   * Minimal init for a single-slide gallery — lazy-load the media
   * when the gallery nears the viewport, but skip all navigation.
   */
  _initSingle() {
    // If already in or near the viewport, load immediately (LCP).
    if (this._isInViewport()) {
      this._loadSlide(0);
      return;
    }

    // Otherwise wait until the gallery scrolls close.
    this._singleObs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this._loadSlide(0);
          this._singleObs.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this._singleObs.observe(this.el);
  }

  _bindEvents() {
    // Arrows
    if (this.arrows.prev) this.arrows.prev.addEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.addEventListener('click', () => this.next());

    // Dots
    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)))
    );

    // Thumbnails
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)))
    );

    // Touch / pointer
    this.viewer.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.viewer.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.viewer.addEventListener('touchend', this._onTouchEnd);
    this.viewer.addEventListener('touchcancel', this._onTouchEnd);

    // Keyboard
    this.el.addEventListener('keydown', this._onKeyDown);

    // Pause autoplay on hover
    this.el.addEventListener('pointerenter', () => {
      if (this.autoplayMs > 0 && !this.autoplayPaused) this._pauseProgress();
    });
    this.el.addEventListener('pointerleave', () => {
      if (this.autoplayMs > 0 && !this.autoplayPaused) this._resumeProgress();
    });

    // Click on slide with video: play
    this.viewer.addEventListener('click', (e) => {
      const btn = e.target.closest('.pg-play-btn');
      if (btn) this._playCurrentVideo();
    });

    // Audio toggle button
    if (this.audioBtn) {
      this.audioBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleAudio();
      });
    }

    // Progress bar animation end
    if (this.progressBar) {
      this.progressBar.addEventListener('animationend', this._onProgressEnd);
    }
  }

  _unbindEvents() {
    if (this.arrows.prev) this.arrows.prev.removeEventListener('click', () => this.prev());
    if (this.arrows.next) this.arrows.next.removeEventListener('click', () => this.next());
    this.dots.forEach((dot) => dot.removeEventListener('click', () => {}));
    this.thumbs.forEach((thumb) => thumb.removeEventListener('click', () => {}));
    this.viewer.removeEventListener('touchstart', this._onTouchStart);
    this.viewer.removeEventListener('touchmove', this._onTouchMove);
    this.viewer.removeEventListener('touchend', this._onTouchEnd);
    this.viewer.removeEventListener('touchcancel', this._onTouchEnd);
    this.el.removeEventListener('keydown', this._onKeyDown);
    if (this.progressBar) {
      this.progressBar.removeEventListener('animationend', this._onProgressEnd);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  Lazy loading
  // ═══════════════════════════════════════════════════════════════

  _loadSlide(idx) {
    const slide = this.slides[idx];
    if (!slide || slide.dataset.pgLoaded === '1') return;

    const media = slide.querySelector('.pg-media');
    if (!media) return;

    const type = slide.dataset.type;

    if (type === 'image') {
      if (media.dataset.srcset) {
        // Responsive images: let the browser pick a candidate via srcset +
        // sizes. Assigning data-src first would start a redundant fetch of
        // the fallback size before srcset takes over.
        media.srcset = media.dataset.srcset;
      } else if (media.dataset.src) {
        media.src = media.dataset.src;
      }
    } else if (type === 'video') {
      if (media.dataset.src && !media.querySelector('source')) {
        const src = document.createElement('source');
        src.src = media.dataset.src;
        src.type = 'video/mp4';
        media.appendChild(src);
      }
      if (media.dataset.poster) {
        media.poster = media.dataset.poster;
      }
    }
    // type === 'model': just load the preview image
    else if (type === 'model') {
      if (media.dataset.src) media.src = media.dataset.src;
    }
    // type === 'external_video': preview already set via inline style

    slide.dataset.pgLoaded = '1';
    slide.classList.add('pg-loaded');

    // Dispatch event
    this.el.dispatchEvent(
      new CustomEvent('pg:load', { detail: { index: idx, type }, bubbles: false })
    );
  }

  _loadAdjacent() {
    for (let i = Math.max(0, this.index - 1); i <= Math.min(this.count - 1, this.index + 1); i++) {
      this._loadSlide(i);
    }
  }

  _initThumbLoading() {
    // Use a separate IntersectionObserver for thumbnails
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            obs.unobserve(img);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    this.el.querySelectorAll('.pg-thumb-img[data-src]').forEach((img) => obs.observe(img));
  }

  // ═══════════════════════════════════════════════════════════════
  //  UI updates
  // ═══════════════════════════════════════════════════════════════

  _updateUI(prevIndex) {
    // Load current & adjacent slides
    this._loadAdjacent();

    // Pause video on previous slide
    if (prevIndex >= 0 && prevIndex !== this.index) {
      this._pauseSlideVideo(prevIndex);
    }

    // Auto-play video on the new current slide (muted, for mobile)
    if (prevIndex !== this.index) {
      this._autoPlaySlideVideo(this.index);
    }

    // Update aria
    this.slides.forEach((s, i) => {
      const active = i === this.index;
      s.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        s.removeAttribute('inert');
      } else {
        s.setAttribute('inert', '');
      }
    });

    // Update dots
    this.dots.forEach((d, i) => {
      d.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });

    // Update thumbnails + scroll into view
    this.thumbs.forEach((t, i) => {
      t.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
      if (i === this.index) {
        t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });

    // Update viewer aria-label
    const activeSlide = this.slides[this.index];
    const activeImg = activeSlide?.querySelector('img');
    if (activeImg?.alt && this.viewer) {
      this.viewer.setAttribute('aria-label', activeImg.alt);
    }

    // Reset autoplay progress
    if (this.autoplayMs > 0 && !this.autoplayPaused) {
      this._restartProgress();
    }

    // Update audio button visibility (only on video slides)
    this._updateAudioButton();

    // Dispatch change event
    this.el.dispatchEvent(
      new CustomEvent('pg:change', { detail: { index: this.index }, bubbles: false })
    );
  }

  // ═══════════════════════════════════════════════════════════════
  //  Video playback
  // ═══════════════════════════════════════════════════════════════

  _getCurrentSlide() {
    return this.slides[this.index];
  }

  _pauseSlideVideo(idx) {
    const slide = this.slides[idx];
    if (!slide) return;

    const video = slide.querySelector('video');
    if (video) {
      video.pause();
      video.onended = null;
      slide.classList.remove('pg-playing');
    }

    // Remove external video iframe
    const iframe = slide.querySelector('iframe');
    if (iframe) {
      iframe.remove();
      slide.classList.remove('pg-playing');
    }

    this.videoPlaying = false;
  }

  /**
   * Auto-play a video slide (muted) when it slides into view.
   * Pauses the slide timer; resumes + advances when the video ends.
   */
  _autoPlaySlideVideo(idx) {
    const slide = this.slides[idx];
    if (!slide) return;

    const type = slide.dataset.type;
    if (type !== 'video' && type !== 'external_video') return;

    // Ensure the slide is loaded
    this._loadSlide(idx);

    if (type === 'video') {
      const video = slide.querySelector('video');
      if (!video) return;

      // Ensure source is loaded
      if (!video.querySelector('source') && video.dataset.src) {
        const src = document.createElement('source');
        src.src = video.dataset.src;
        src.type = 'video/mp4';
        video.appendChild(src);
      }
      if (video.dataset.poster && video.poster.startsWith('data:')) {
        video.poster = video.dataset.poster;
      }

      // Default muted for autoplay (required on mobile Safari / Chrome Android).
      // If the user has enabled audio via the unmute button, try unmuted
      // and fall back to muted if the browser blocks it.
      if (this.audioEnabled) {
        video.muted = false;
        video.removeAttribute('muted');
      } else {
        video.muted = true;
        video.setAttribute('muted', '');
      }

      this.videoPlaying = true;
      this._clearAutoplay(); // pause slide timer while video plays

      video.play().then(() => {
        slide.classList.add('pg-playing');
      }).catch(() => {
        // Audio autoplay blocked — fall back to muted
        if (this.audioEnabled) {
          video.muted = true;
          video.setAttribute('muted', '');
          return video.play().then(() => slide.classList.add('pg-playing'));
        }
        // If still blocked, give up
        this.videoPlaying = false;
        this._scheduleAutoplay();
      });

      // When the video finishes: advance to next slide, resume autoplay
      video.onended = () => {
        slide.classList.remove('pg-playing');
        this.videoPlaying = false;
        if (this.autoplayMs > 0) {
          this.next();
          if (!this.autoplayPaused) this._scheduleAutoplay();
        }
      };
    } else if (type === 'external_video') {
      // External videos (YouTube/Vimeo) don't auto-play silently —
      // they need a user gesture. Skip auto-play; keep the preview showing.
      // The user can tap the play button to start them.
    }
  }

  _playCurrentVideo() {
    const slide = this._getCurrentSlide();
    if (!slide) return;

    const type = slide.dataset.type;

    // Block the slide timer while the user is watching the video,
    // but keep autoplayPaused unchanged so swiping away resumes normally.
    this.videoPlaying = true;
    this._clearAutoplay();

    if (type === 'video') {
      const video = slide.querySelector('video');
      if (!video) return;

      // Ensure source is loaded
      this._loadSlide(this.index);

      if (!video.querySelector('source') && video.dataset.src) {
        const src = document.createElement('source');
        src.src = video.dataset.src;
        src.type = 'video/mp4';
        video.appendChild(src);
      }
      if (video.dataset.poster && video.poster.startsWith('data:')) {
        video.poster = video.dataset.poster;
      }

      // User explicitly tapped play — enable audio globally
      this.audioEnabled = true;
      this._updateAudioButton();

      video.muted = false;
      video.removeAttribute('muted');

      video.play().then(() => {
        slide.classList.add('pg-playing');
      }).catch(() => {
        // Sound may be blocked; fall back to muted
        video.muted = true;
        video.setAttribute('muted', '');
        video.play().then(() => slide.classList.add('pg-playing')).catch(() => {
          this.videoPlaying = false;
          this._scheduleAutoplay();
        });
      });

      // When the video finishes: advance, resume slide timer
      video.onended = () => {
        slide.classList.remove('pg-playing');
        this.videoPlaying = false;
        if (this.autoplayMs > 0) {
          this.next();
          if (!this.autoplayPaused) this._scheduleAutoplay();
        }
      };
    } else if (type === 'external_video') {
      // User tapped play — enable audio globally for subsequent native videos
      this.audioEnabled = true;
      this._updateAudioButton();

      const wrapper = slide.querySelector('.pg-ext-vid');
      if (!wrapper || slide.querySelector('iframe')) {
        this.videoPlaying = false;
        return;
      }

      const embedUrl = wrapper.dataset.embed;
      if (!embedUrl) {
        this.videoPlaying = false;
        return;
      }

      // Build autoplay URL (user tapped play so autoplay=1 is allowed)
      let url = embedUrl;
      const sep = url.includes('?') ? '&' : '?';
      url += `${sep}autoplay=1&mute=1&controls=1`;
      if (wrapper.dataset.host === 'youtube') url += '&rel=0';

      const iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.allow =
        'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = slide.getAttribute('aria-label') || 'Video';
      wrapper.appendChild(iframe);
      slide.classList.add('pg-playing');

      // External iframes don't fire an 'ended' event we can catch reliably,
      // so release the videoPlaying lock after a generous timeout.
      this._extVidTimeout = setTimeout(() => {
        this.videoPlaying = false;
        if (!this.autoplayPaused) this._scheduleAutoplay();
      }, 5 * 60 * 1000); // 5 min max
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  Audio toggle
  // ═══════════════════════════════════════════════════════════════

  /**
   * Toggle audio on/off for the current video and all future autoplays.
   * On mobile, unmuting autoplay may still be blocked by the browser —
   * we fall back to muted gracefully in _autoPlaySlideVideo.
   */
  _toggleAudio() {
    this.audioEnabled = !this.audioEnabled;

    // Apply to the currently playing video (if any)
    const slide = this._getCurrentSlide();
    if (slide && slide.dataset.type === 'video') {
      const video = slide.querySelector('video');
      if (video) {
        if (this.audioEnabled) {
          video.muted = false;
          video.removeAttribute('muted');
          // Try to play with audio — may be blocked on mobile
          video.play().catch(() => {
            video.muted = true;
            video.setAttribute('muted', '');
          });
        } else {
          video.muted = true;
          video.setAttribute('muted', '');
        }
      }
    }

    this._updateAudioButton();
  }

  /**
   * Show/hide the audio button based on current slide type and
   * toggle its icon between muted / unmuted.
   */
  _updateAudioButton() {
    if (!this.audioBtn) return;

    const slide = this._getCurrentSlide();
    if (!slide) return;

    const type = slide.dataset.type;
    const isVideo = type === 'video' || type === 'external_video';

    if (isVideo) {
      this.audioBtn.removeAttribute('hidden');
      this.audioBtn.classList.toggle('pg-audio--on', this.audioEnabled);
      this.audioBtn.setAttribute(
        'aria-label',
        this.audioEnabled ? 'Mute video' : 'Unmute video'
      );
    } else {
      this.audioBtn.setAttribute('hidden', '');
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  Touch / swipe handling
  // ═══════════════════════════════════════════════════════════════

  _onTouchStart(e) {
    if (e.touches.length > 1) return;
    const t = e.touches[0];
    this.touch.startX = t.clientX;
    this.touch.startY = t.clientY;
    this.touch.deltaX = 0;
    this.touch.time = Date.now();
    this.dragging = false;
    this.track.classList.add('pg-dragging');
    this._clearAutoplay();
  }

  _onTouchMove(e) {
    const t = e.touches[0];
    const dx = t.clientX - this.touch.startX;
    const dy = t.clientY - this.touch.startY;

    // Let vertical scroll win
    if (!this.dragging && Math.abs(dy) > Math.abs(dx)) {
      this.track.classList.remove('pg-dragging');
      return;
    }

    // Threshold to start dragging (prevents accidental micro-drags)
    if (!this.dragging && Math.abs(dx) < 8) return;

    if (!this.dragging) {
      this.dragging = true;
      e.preventDefault();
    } else {
      e.preventDefault();
    }

    this.touch.deltaX = dx;

    // Direct manipulation: translate track with finger
    const pct = -this.index * 100 + (dx / this.viewer.offsetWidth) * 100;

    // Rubber-band at edges when not looping
    if (!this.loop) {
      if (this.index === 0 && dx > 0) {
        this.track.style.transform = `translateX(${Math.min(pct, dx * 0.3 / this.viewer.offsetWidth * 100)}%)`;
        return;
      }
      if (this.index === this.count - 1 && dx < 0) {
        const over = Math.abs(dx) * 0.3 / this.viewer.offsetWidth * 100;
        this.track.style.transform = `translateX(${-this.index * 100 - over}%)`;
        return;
      }
    }

    this.track.style.transform = `translateX(${pct}%)`;
  }

  _onTouchEnd() {
    this.track.classList.remove('pg-dragging');
    if (!this.dragging) {
      this._scheduleAutoplay();
      return;
    }

    this.dragging = false;
    const dx = this.touch.deltaX;
    const dt = Date.now() - this.touch.time;
    const velocity = dt > 0 ? Math.abs(dx) / dt : 0; // px/ms
    const threshold = this.viewer.offsetWidth * 0.2;

    // Re-enable CSS transition for snap
    this.track.style.transition = '';
    this.track.offsetHeight; // force reflow

    if (Math.abs(dx) > threshold || velocity > 0.5) {
      dx < 0 ? this.next() : this.prev();
    } else {
      // Snap back
      this.track.style.transform = `translateX(${-this.index * 100}%)`;
    }

    this._scheduleAutoplay();
  }

  // ═══════════════════════════════════════════════════════════════
  //  Keyboard navigation
  // ═══════════════════════════════════════════════════════════════

  _onKeyDown(e) {
    // Only respond when gallery or its children are focused
    if (!this.el.contains(document.activeElement)) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        break;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  Autoplay
  // ═══════════════════════════════════════════════════════════════

  _scheduleAutoplay() {
    this._clearAutoplay();
    if (this.autoplayMs <= 0 || this.autoplayPaused || this.videoPlaying || this.count < 2) return;
    this.autoplayTimer = setTimeout(() => this.next(), this.autoplayMs);
    this._startProgress();
  }

  _clearAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this._stopProgress();
  }

  _startProgress() {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('pg-progress--running');
    this.progressBar.style.setProperty('--pg-autoplay-ms', `${this.autoplayMs}ms`);
    // Force reflow to restart animation
    void this.progressBar.offsetWidth;
    this.progressBar.classList.add('pg-progress--running');
  }

  _stopProgress() {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('pg-progress--running');
  }

  _pauseProgress() {
    if (!this.progressBar || !this.progressBar.classList.contains('pg-progress--running')) return;
    this.progressBar.style.animationPlayState = 'paused';
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  _resumeProgress() {
    if (!this.progressBar || !this.progressBar.classList.contains('pg-progress--running')) return;
    if (this.videoPlaying) return; // don't restart timer while a video is playing
    this.progressBar.style.animationPlayState = 'running';
    // Estimate remaining time from animation progress, then schedule
    const remaining = this.autoplayMs * 0.8; // rough estimate
    if (!this.autoplayTimer) {
      this.autoplayTimer = setTimeout(() => this.next(), Math.max(remaining, 500));
    }
  }

  _restartProgress() {
    this._clearAutoplay();
    if (this.autoplayMs <= 0 || this.autoplayPaused || this.videoPlaying || this.count < 2) return;
    this.autoplayTimer = setTimeout(() => this.next(), this.autoplayMs);
    this._startProgress();
  }

  _onProgressEnd() {
    // CSS animation finished → advance
    this.next();
  }

  _isInViewport() {
    const rect = this.el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }
}

// ── Auto-initialize ─────────────────────────────────────────────
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('.pg-gallery').forEach((el) => { el.pgInstance = new ProductGallery(el); });
    });
  } else {
    document.querySelectorAll('.pg-gallery').forEach((el) => { el.pgInstance = new ProductGallery(el); });
  }
}

// Expose globally for manual instantiation
if (typeof window !== 'undefined') {
  window.ProductGallery = ProductGallery;
}